import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { environment } from '../../../environments/environment';
import { PermissionService } from '../services/permission/permission.service';
import { HeaderEnum, PermissionEnum } from '../../../environments/shared-environment/permission';
import { RETURN_URL_QUERY_PARAM } from '../../../environments/shared-environment/config';

const PUBLIC_ROUTES = [
  environment.configs.unauthenticatedRoute,
  '/forgot/password',
  '/reset/password',
];

export function sanitizeReturnUrl(url: unknown): string | null {
  if (typeof url !== 'string' || !url)
    return null;

  let decoded = url;

  try {
    decoded = decodeURIComponent(url);
  } catch {
    return null;
  }

  if (!decoded.startsWith('/') || decoded.startsWith('//') || decoded.includes('://'))
    return null;

  const path = decoded.split('?')[0];

  if (PUBLIC_ROUTES.includes(path))
    return null;

  return decoded;
}

export const isAuthenticated: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isLoggedIn())
    return router.navigate(
      [environment.configs.unauthenticatedRoute],
      { queryParams: { [RETURN_URL_QUERY_PARAM]: state.url }, replaceUrl: true },
    ).then(() => false);

  return true;
};

export const notAuthenticated = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isLoggedIn())
    return true

  return false;
}

export const canLogin: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isLoggedIn()) {
    const returnUrl = sanitizeReturnUrl(route.queryParams[RETURN_URL_QUERY_PARAM]);
    const target = returnUrl ?? environment.configs.authenticatedRoute;

    return router.navigateByUrl(target, { replaceUrl: true }).then(() => false);
  }

  return true;
};

export const canActivate = (header: HeaderEnum, permission: PermissionEnum) => {
  const permissionService = inject(PermissionService);
  const can = permissionService.has(header, permission);
  const router = inject(Router);

  if (!can)
    return router.navigate(['/main/documents/comparison/compare']);

  return can;
}
